import type { Distance, Pixel, Rectangle, SpatialRectangle } from "@pdfking/math"
import { ToRgb } from "@pdfking/math"
import { CreateMixin } from "@pdfking/reactivity/preact"
import type { ColorID, PlotMedia, PlottingContext } from "@pdfking/renderer-api"
import { ConvertDistances, ToPixels } from "@pdfking/renderer-api"

import type { RequestMedia } from "#public"

import { IDMixin } from "./IDMixin"

export const PlotMixin = CreateMixin({
  methods: {
    get_color(id: ColorID, fallback?: string) {
      if (id) {
        const c = ToRgb(this.context.color_map[id])
        return `rgba(${c.red * 255}, ${c.green * 255}, ${c.blue * 255}, ${c.alpha})`
      }
      return fallback || "transparent"
    },
    get_h(rect: Rectangle<Distance>): Pixel {
      return ToPixels(this.context, rect.height)
    },
    get_measures(rect: SpatialRectangle<Distance>): SpatialRectangle<Pixel> {
      return ConvertDistances(this.context, rect)
    },
    get_w(rect: Rectangle<Distance>): Pixel {
      return ToPixels(this.context, rect.width)
    },
    to_px(value: Distance): Pixel {
      return ToPixels(this.context, value)
    },
    // Everything a shape draws lives inside the page's flipped group (see
    // PlotPage), so coordinates are the API's own: y up from the trim's
    // bottom-left. A box is placed by moving to its corner, nothing more —
    // rotation belongs to the enclosing `plot_group`.
    transform_coords(position: SpatialRectangle<Distance>, offset_x?: number, offset_y?: number) {
      const { x, y } = this.get_measures(position)
      return `translate(${x + (offset_x || 0)},${y + (offset_y || 0)})`
    },
    // Glyphs and rasters would come out mirrored in a y-up space, so they undo
    // the flip about their own centre — which leaves their placement alone.
    unflip(cx: number, cy: number) {
      return `translate(${cx} ${cy}) scale(1 -1)`
    },
  },
  mixins: [IDMixin] as const,
  props: {
    context: {} as PlottingContext<unknown>,
    // The media filling a slot, and its raster on demand. A function, not a
    // value: the split streams in page by page, and only the media that read
    // it (inside their own computed) should follow — not the context every
    // shape measures with. Asked by each media as it mounts — which only
    // happens once its page has scrolled into view (see PlotPage) — so a
    // raster is made for the pages actually looked at.
    request_media: undefined as RequestMedia | undefined,
    // The element is handed over with the action: a shape's own coordinates
    // are local to the `plot_group`s above it, so the only thing that knows
    // where it actually landed is the DOM.
    media_click: undefined as
      | ((id: string, action: PlotMedia, element: SVGGraphicsElement) => void)
      | undefined,
    printable: false,
  },
})
